import React from 'react';
import { Channel } from '@meradio/shared-types';
import { useChannelSearch } from '../hooks/useChannelSearch';
import ChannelCard from './ChannelCard';
import { Button, IconButton, Input } from '@/components/ui';

interface ChannelSearchProps {
  onChannelSelect?: (channel: Channel) => void;
  maxResults?: number;
  placeholder?: string;
  className?: string;
}

const ChannelSearch: React.FC<ChannelSearchProps> = ({
  onChannelSelect,
  maxResults = 25,
  placeholder = "Search YouTube channels...",
  className = "",
}) => {
  const {
    query,
    setQuery,
    isSearching,
    searchResults,
    searchError,
    hasSearched,
    clearResults,
    triggerSearch,
  } = useChannelSearch({ maxResults });

  const handleChannelSelect = (channel: Channel) => {
    if (onChannelSelect) {
      onChannelSelect(channel);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      triggerSearch();
    }
  };

  const handleClear = () => {
    clearResults();
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="relative">
        <div className="relative">
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="pr-12 text-base"
            autoComplete="off"
            spellCheck={false}
            aria-label="Search YouTube channels"
            disabled={isSearching}
            leftIcon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            }
          />

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {isSearching ? (
              <div className="w-5 h-5 border-2 border-meradio-primary border-t-transparent rounded-full animate-spin" />
            ) : query.length > 0 ? (
              <IconButton onClick={handleClear} size="sm" variant="ghost" aria-label="Clear search">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </IconButton>
            ) : null}
          </div>
        </div>

        {query.length > 0 && query.length < 2 && (
          <p className="mt-2 text-sm text-meradio-dark-light dark:text-text-dark-secondary">
            Type at least 2 characters to search...
          </p>
        )}
      </div>

      <div className="mt-6">
        {isSearching && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark rounded-xl p-4 animate-pulse"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-16 h-16 bg-meradio-light rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-meradio-light rounded w-3/4" />
                    <div className="h-3 bg-meradio-light rounded w-full" />
                    <div className="h-3 bg-meradio-light rounded w-2/3" />
                  </div>
                  <div className="w-20 h-8 bg-meradio-light rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {searchError && !isSearching && (
          <div className="bg-meradio-error/10 border border-meradio-error/20 rounded-xl p-4 text-center">
            <div className="text-meradio-error text-sm font-medium mb-2">
              Search Error
            </div>
            <p className="text-meradio-dark-light dark:text-text-dark-secondary text-sm">
              {searchError}
            </p>
            <Button variant="link" size="sm" onClick={() => triggerSearch()} className="mt-3">
              Try Again
            </Button>
          </div>
        )}

        {!isSearching && !searchError && hasSearched && (
          <>
            {searchResults.length > 0 ? (
              <>
                <div className="mb-4 text-sm text-meradio-dark-light dark:text-text-dark-secondary">
                  Found {searchResults.length} channel{searchResults.length !== 1 ? 's' : ''} for "{query}"
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {searchResults.map((channel) => (
                    <ChannelCard
                      key={channel.channelId}
                      channel={channel}
                      onChannelClick={handleChannelSelect}
                      showFollowButton={true}
                      size="md"
                      className="hover:scale-105"
                      previewEnabled
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-semibold text-meradio-dark dark:text-text-dark-primary mb-2">
                  No channels found
                </h3>
                <p className="text-meradio-dark-light dark:text-text-dark-secondary mb-4">
                  No YouTube channels found for "{query}". Try a different search term.
                </p>
                <Button variant="link" size="sm" onClick={handleClear}>
                  Clear Search
                </Button>
              </div>
            )}
          </>
        )}

        {!hasSearched && !isSearching && query.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎵</div>
            <h3 className="text-lg font-semibold text-meradio-dark dark:text-text-dark-primary mb-2">
              Discover Music Channels
            </h3>
            <p className="text-meradio-dark-light dark:text-text-dark-secondary max-w-md mx-auto">
              Search for your favorite YouTube music channels to follow and automatically import their latest tracks.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelSearch;
